import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Merchants from './pages/Merchants';
import MerchantForm from './pages/MerchantForm';
import PendingMerchants from './pages/PendingMerchants';
import Settlement from './pages/Settlement';
import SettlementVerify from './pages/SettlementVerify';
import Notifications from './pages/Notifications';
import NotificationsLog from './pages/NotificationsLog';
import Dormant from './pages/Dormant';
import Renewals from './pages/Renewals';
import Users from './pages/Users';
import SecuritySessions from './pages/SecuritySessions';
import SecurityEvents from './pages/SecurityEvents';
import SecurityErrors from './pages/SecurityErrors';
import ChangePassword from './pages/ChangePassword';
import Layout from './components/Layout';
import { api, getToken, setToken, type AuthUser } from './api';

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 입력 필드는 한글 시작을 기본으로 — 모든 text/textarea에 lang="ko" 자동 부여.
  // 표준상 사이트가 IME를 강제로 켜는 방법은 없지만, lang="ko"가 있으면 윈도우 한국어 OS에서 브라우저가 IME를 자동 전환해주는 동작이 많다.
  // 80개 input을 개별 패치하지 않고 전역 effect로 처리 — 새로 마운트되는 모달/폼에도 적용되도록 MutationObserver 사용.
  useEffect(() => {
    const NON_TEXT_TYPES = new Set([
      'number','date','time','datetime-local','month','week',
      'checkbox','radio','file','color','range','submit','button','reset','hidden','image',
    ]);
    const apply = (el: Element) => {
      if (el.tagName === 'INPUT') {
        const t = (el as HTMLInputElement).type;
        if (NON_TEXT_TYPES.has(t)) return;
      } else if (el.tagName !== 'TEXTAREA') {
        return;
      }
      if (!el.hasAttribute('lang')) el.setAttribute('lang', 'ko');
    };
    const scan = (root: ParentNode) => root.querySelectorAll('input, textarea').forEach(apply);
    scan(document);
    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        m.addedNodes.forEach(n => {
          if (!(n instanceof Element)) return;
          apply(n);
          scan(n);
        });
      }
    });
    mo.observe(document.body, { subtree: true, childList: true });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      try {
        const r = await api<{ user: AuthUser }>('GET', '/auth/me');
        if (active) setUser(r.user);
      } catch {
        setToken(null);
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>로딩…</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to="/" replace /> : <Login onLogin={(u) => { setUser(u); navigate(u.must_change_password ? '/change-password' : '/'); }} />
      } />
      <Route element={user ? <Layout user={user} onLogout={() => { setToken(null); setUser(null); }} /> : <Navigate to="/login" replace />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/merchants" element={<Merchants />} />
        <Route path="/merchants/new" element={<MerchantForm />} />
        <Route path="/merchants/:mid" element={<MerchantForm />} />
        <Route path="/pending-merchants" element={<PendingMerchants />} />
        <Route path="/settlement" element={<Navigate to="/settlement/discount" replace />} />
        <Route path="/settlement/discount" element={<Settlement mode="discount" />} />
        <Route path="/settlement/realtime" element={<Settlement mode="realtime" />} />
        <Route path="/settlement/verify"   element={<SettlementVerify />} />
        <Route path="/notifications"     element={user?.role === 'admin' ? <Notifications /> : <Navigate to="/" />} />
        <Route path="/notifications-log" element={user?.role === 'admin' ? <NotificationsLog /> : <Navigate to="/" />} />
        <Route path="/dormant" element={<Dormant />} />
        <Route path="/renewals" element={<Renewals />} />
        <Route path="/users" element={user?.role === 'admin' ? <Users /> : <Navigate to="/" />} />
        <Route path="/security/sessions" element={user?.role === 'admin' ? <SecuritySessions /> : <Navigate to="/" />} />
        <Route path="/security/events"   element={user?.role === 'admin' ? <SecurityEvents />   : <Navigate to="/" />} />
        <Route path="/security/errors"   element={user?.role === 'admin' ? <SecurityErrors />   : <Navigate to="/" />} />
        <Route path="/change-password" element={<ChangePassword onDone={() => navigate('/')} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
